import {CommentsCategory} from '@domain/comments'
import {ApiClient} from '@hconnect/apiclient'
import {AttachmentFile} from '@hconnect/common/types'
import {attachmentFilesToFormData} from '@hconnect/common/utils'
import {kebabCase} from 'lodash'

import {PlannerForecastType, AssetOperationTimeSource, ShippingType} from '../enums'
import type {
  ScheduleItem,
  Schedule,
  SubmitSchedule,
  Comment,
  CreateEditComment,
  ScheduleCostAvoidance,
  CreateMaterialOrder,
  MaterialOrder,
  EditMaterialOrder,
  CreateUpdateScheduleItem,
  UpdateScheduleItemResponse,
  RecalculatePlanDTO
} from '../interfaces/api'
import {OptimizerSettingsDTO} from '../interfaces/api/optimizer'

import {dsApiBasePath, type MutateFn} from './apiClient'

export interface Mutations {
  //  C# Endpoints
  sendToOptimizedPlan: MutateFn<[{plantCode: string; scheduleId: number}], void>
  addScheduleItem: MutateFn<
    [{plantCode: string; scheduleId: number; scheduleItemDTO: CreateUpdateScheduleItem}],
    void
  >
  updateScheduleItem: MutateFn<
    [
      {
        plantCode: string
        scheduleId: number
        scheduleItemDTO: CreateUpdateScheduleItem
        scheduleItemId: string
      }
    ],
    UpdateScheduleItemResponse
  >
  deleteScheduleItem: MutateFn<
    [{plantCode: string; scheduleId: number; scheduleItem: ScheduleItem}],
    void
  >
  removeAutofillItems: MutateFn<[{plantCode: string; start: string; end: string}], void>
  submitElectricityPlan: MutateFn<
    [{plantCode: string; scheduleId: number; updatedById: string}],
    SubmitSchedule
  >
  sendBackupPlan: MutateFn<[{plantCode}], void>
  addComment: MutateFn<
    [
      {
        plantCode: string
        commentsCategory: CommentsCategory
        commentsCategoryInstanceId: string
        commentDto: CreateEditComment
      }
    ],
    Comment
  >
  editComment: MutateFn<
    [
      {
        plantCode: string
        commentsCategory: CommentsCategory
        commentsCategoryInstanceId: string
        commentId: string
        commentDto: CreateEditComment
      }
    ],
    void
  >
  deleteComment: MutateFn<
    [
      {
        plantCode: string
        commentsCategory: CommentsCategory
        commentsCategoryInstanceId: string
        commentId: string
      }
    ],
    void
  >
  dispatchAutofill: MutateFn<
    [
      {
        plantCode: string
        config: {startTime: string; endTime: string; forecastType: PlannerForecastType}
      }
    ],
    void
  >
  calculateCostAvoidance: MutateFn<[{plantCode: string; scheduleId: number}], ScheduleCostAvoidance>
  addMaterialOrder: MutateFn<[{plantCode: string; dto: CreateMaterialOrder}], MaterialOrder>
  editMaterialOrder: MutateFn<
    [
      {
        plantCode: string
        materialOrderId: number
        editedOrder: EditMaterialOrder
        property: keyof EditMaterialOrder
      }
    ],
    MaterialOrder
  >
  cancelMaterialOrder: MutateFn<[{plantCode: string; materialOrderId: number}], void>
  deleteMaterialOrder: MutateFn<[{plantCode: string; materialOrderId: number}], void>
  createStockLevelOverwrite: MutateFn<
    [{plantCode: string; materialId: number; date: string; level: number}],
    void
  >
  addMaterialOrderAttachments: MutateFn<
    [{plantCode: string; materialOrderId: number; attachments: AttachmentFile[]}],
    void
  >
  deleteMaterialOrderAttachments: MutateFn<
    [{plantCode: string; materialOrderId: number; attachmentIds: number[]}],
    void
  >
  createDemandOverride: MutateFn<
    [
      {
        plantCode: string
        materialId: number
        date: string
        shippingType: ShippingType
        quantity: number
      }
    ],
    void
  >
  uploadSalesForecast: MutateFn<[{plantCode: string; file: AttachmentFile}], void>
  editOptimizerSettings: MutateFn<[{plantCode: string; settings: OptimizerSettingsDTO}], void>
  recalculatePlan: MutateFn<[{plantCode: string; dto: RecalculatePlanDTO}], void>
  cancelOptimizerRun: MutateFn<[{plantCode: string; runId: number}], void>
  analyzeProductionPlan: MutateFn<[{plantCode: string}], void>
}

export const mutations: Mutations = {
  //  C# Endpoints
  sendToOptimizedPlan: async ({plantCode, scheduleId}) => {
    const path = `/plants/${plantCode}/schedules/optimized/${scheduleId}`
    await ApiClient.axiosInstance.patch<Schedule>(path, {})
  },
  addScheduleItem: async ({scheduleId, plantCode, scheduleItemDTO: scheduleItem}) => {
    const path = `/schedules/${scheduleId}/items`
    const params: Record<string, unknown> = {plantCode}
    await ApiClient.axiosInstance.post<void>(path, scheduleItem, {params})
  },
  updateScheduleItem: async ({scheduleId, plantCode, scheduleItemDTO, scheduleItemId}) => {
    const path = `/schedules/${scheduleId}/items/${scheduleItemId}`
    const params: Record<string, unknown> = {plantCode}
    const response = await ApiClient.axiosInstance.put<UpdateScheduleItemResponse>(
      path,
      scheduleItemDTO,
      {params}
    )
    return response.data
  },
  deleteScheduleItem: async ({scheduleId, plantCode, scheduleItem}) => {
    const path = `/schedules/${scheduleId}/items/${scheduleItem.id}`
    const params: Record<string, unknown> = {plantCode}
    await ApiClient.axiosInstance.delete(path, {params})
  },
  removeAutofillItems: async ({plantCode, start, end}) => {
    const path = `/plants/${plantCode}/schedules/current/items`
    await ApiClient.axiosInstance.delete(path, {
      params: {start, end, source: AssetOperationTimeSource.Optimizer}
    })
  },
  submitElectricityPlan: async ({scheduleId, plantCode, updatedById}) => {
    const path = '/electricity'
    const params: Record<string, unknown> = {
      plantCode,
      scheduleId,
      updatedBy: updatedById
    }
    const response = await ApiClient.axiosInstance.post<SubmitSchedule>(path, {}, {params})
    return response.data
  },
  sendBackupPlan: async ({plantCode}) => {
    const path = '/electricity/production-planning-backup'
    const params: Record<string, unknown> = {plantCode}
    const response = await ApiClient.axiosInstance.post<void>(path, {}, {params})
    return response.data
  },
  addComment: async ({plantCode, commentsCategory, commentsCategoryInstanceId, commentDto}) => {
    const path = `/comments/${plantCode}/${commentsCategory}/${commentsCategoryInstanceId}`
    const response = await ApiClient.axiosInstance.post<Comment>(path, commentDto)
    return response.data
  },
  editComment: async ({
    plantCode,
    commentsCategory,
    commentsCategoryInstanceId,
    commentId,
    commentDto
  }) => {
    const path = `/comments/${plantCode}/${commentsCategory}/${commentsCategoryInstanceId}/${commentId}`
    await ApiClient.axiosInstance.put<void>(path, commentDto)
  },
  deleteComment: async ({plantCode, commentsCategory, commentsCategoryInstanceId, commentId}) => {
    const path = `/comments/${plantCode}/${commentsCategory}/${commentsCategoryInstanceId}/${commentId}`
    await ApiClient.axiosInstance.delete<void>(path)
  },
  dispatchAutofill: async ({plantCode, config}) => {
    const path = `${dsApiBasePath}/v2/autofill/${plantCode}`
    await ApiClient.axiosInstance.post<void>(path, {
      start_time: config.startTime,
      end_time: config.endTime,
      forecast_type: config.forecastType
    })
  },
  calculateCostAvoidance: async ({plantCode, scheduleId}) => {
    const path = `/plants/${plantCode}/schedules/${scheduleId}/kpi/cost-avoidance`
    const response = await ApiClient.axiosInstance.get<ScheduleCostAvoidance>(path)
    return response.data
  },
  addMaterialOrder: async ({plantCode, dto}) => {
    const path = `/plants/${plantCode}/material-orders`
    const response = await ApiClient.axiosInstance.post<MaterialOrder>(path, dto)
    return response.data
  },
  editMaterialOrder: async ({plantCode, materialOrderId, editedOrder, property}) => {
    const path = `/plants/${plantCode}/material-orders/${materialOrderId}/${kebabCase(property)}`
    const response = await ApiClient.axiosInstance.patch<MaterialOrder>(path, {
      [property]: editedOrder[property]
    })
    return response.data
  },
  cancelMaterialOrder: async ({plantCode, materialOrderId}) => {
    const path = `/plants/${plantCode}/material-orders/${materialOrderId}/cancel`
    await ApiClient.axiosInstance.patch<void>(path)
  },
  deleteMaterialOrder: async ({plantCode, materialOrderId}) => {
    const path = `/plants/${plantCode}/material-orders/${materialOrderId}`
    await ApiClient.axiosInstance.delete<void>(path)
  },
  addMaterialOrderAttachments: async ({plantCode, materialOrderId, attachments}) => {
    const path = `/plants/${plantCode}/material-orders/${materialOrderId}/attachments`
    const formData = new FormData()
    await attachmentFilesToFormData(attachments, formData)
    await ApiClient.axiosInstance.post<void>(path, formData)
  },
  deleteMaterialOrderAttachments: async ({plantCode, materialOrderId, attachmentIds}) => {
    const getPath = (attachmentId: number) =>
      `/plants/${plantCode}/material-orders/${materialOrderId}/attachments/${attachmentId}`
    await Promise.all(attachmentIds.map((id) => ApiClient.axiosInstance.delete<void>(getPath(id))))
  },
  createStockLevelOverwrite: async ({plantCode, materialId, date, level}) => {
    const path = `/plants/${plantCode}/material-storage/stock-level/material/${materialId}`
    await ApiClient.axiosInstance.post<void>(path, {date, level})
  },
  createDemandOverride: async ({materialId, date, shippingType, plantCode, quantity}) => {
    const path = `/plants/${plantCode}/material-demand/${materialId}/demand/overrides/${date}/shipping/${shippingType}`
    const response = await ApiClient.axiosInstance.post<void>(path, {quantity})
    return response.data
  },
  uploadSalesForecast: async ({plantCode, file}) => {
    const path = `/plants/${plantCode}/material-demand/sales-forecast`
    if (!file.originalFile) {
      throw new Error('File is missing')
    }
    const formData = new FormData()
    formData.append('salesForecastFile', file.originalFile, file.name)
    await ApiClient.axiosInstance.post<void>(path, formData)
  },
  editOptimizerSettings: async ({plantCode, settings}) => {
    const path = `${dsApiBasePath}/v2/optimizer/${plantCode}/settings`
    await ApiClient.axiosInstance.patch<void>(path, settings)
  },
  recalculatePlan: async ({plantCode, dto}) => {
    const path = `${dsApiBasePath}/v2/optimizer/${plantCode}/runs`
    await ApiClient.axiosInstance.post<void>(path, dto)
  },
  cancelOptimizerRun: async ({plantCode, runId}) => {
    const path = `${dsApiBasePath}/v2/optimizer/${plantCode}/runs/${runId}`
    await ApiClient.axiosInstance.delete<void>(path)
  },
  analyzeProductionPlan: async ({plantCode}) => {
    const path = `${dsApiBasePath}/v2/optimizer/${plantCode}/run-analysis`
    await ApiClient.axiosInstance.post<void>(path, {})
  }
}
